import Sentry from 'application/exception_reporter';
import { useEffect, useCallback, useState, useRef } from 'react';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useTranslation } from 'react-i18next';

const getSavedCards = async (checkoutClient) => {
  try {
    const data = await checkoutClient.getSavedCards();
    return data['wallet_payment_sources'];
  } catch (error) {
    console.error('Error fetching saved cards:', error);
    Sentry.capture(error);
    return [];
  }
};

const deleteSavedCard = async (cardId, checkoutClient, setToast, t) => {
  try {
    await checkoutClient.deleteSavedCard(cardId);
    return true;
  } catch (error) {
    // TODO (dadamo): Internationalize this in follow-up PR
    setToast({ open: false });
    setToast({
      message: t('checkout.payment.deleteSavedCardError'),
      variant: 'warning',
      timeout: 3000,
      open: true,
    });
    console.error('Error deleting saved card:', error);
    Sentry.capture(error);
    return false;
  }
};

// TODO (dadamo): Consider lifting a lot of this state further up the component tree
//  possibly into some sort of "saved cards" context provider
export const useSavedCards = (
  setShowCardForm,
  sqCard,
  canUseSavedCard,
  setSelectedCard,
) => {
  const [savedCards, setSavedCards] = useState([]);
  const [showSavedCards, setShowSavedCards] = useState(false);
  const [deletionInProgress, setDeletionInProgress] = useState(false);

  const savedCardsDropdownRef = useRef(null);

  const { checkoutClient, setToast, setPayWithSavedCard } = useCheckoutFlow();

  const { t } = useTranslation();

  const onCardSelected = useCallback(
    (cardId) => {
      const foundCard = savedCards.find(
        (card) => String(card.id) === String(cardId),
      );
      if (foundCard) {
        setSelectedCard(foundCard);
        setPayWithSavedCard(true);
        setShowCardForm(false);
      } else {
        setSelectedCard(null);
        setPayWithSavedCard(false);
      }
      setShowSavedCards(false);
    },
    [setShowCardForm, setSelectedCard, setShowSavedCards, savedCards],
  );

  const onCardDeleted = useCallback(
    (cardId) => {
      setDeletionInProgress(true);
      deleteSavedCard(cardId, checkoutClient, setToast, t)
        .then((result) => {
          if (result) {
            setSavedCards(savedCards.filter((card) => card.id !== cardId));
          }
        })
        .finally(() => {
          setDeletionInProgress(false);
          setShowSavedCards(true);
        });
    },
    [setDeletionInProgress, setShowSavedCards, setSavedCards, savedCards],
  );

  const onCardClicked = useCallback(
    (event) => {
      setShowCardForm(true);
      setSelectedCard(null);
      setPayWithSavedCard(false);
      if (sqCard) {
        // This `setTimeout` is to reduce the likelihood of calling the recalculateSize method
        // before the card form is fully rendered
        setTimeout(async () => {
          await sqCard.clear();
          await sqCard.focus('cardNumber');
          await sqCard.recalculateSize();
        }, 10);
      }

      // This is an ugly hack to make sure the "market action card" for the
      // selected card does not stay in the deselected state after it is clicked
      // eslint-disable-next-line no-param-reassign
      event.target.selected = true;
    },
    [setShowCardForm, setSelectedCard, sqCard],
  );

  const showSavedCardsDropdown = useCallback(
    (event) => {
      if (event.detail.field === 'cardNumber') {
        setShowSavedCards(true);
      }
    },
    [setShowSavedCards],
  );

  const hideSavedCardsDropdown = useCallback(() => {
    setShowSavedCards(false);
  }, [setShowSavedCards]);

  const handleClickOutside = useCallback(
    (event) => {
      if (
        savedCardsDropdownRef.current &&
        !savedCardsDropdownRef.current.contains(event.target)
      ) {
        setShowSavedCards(false);
      }
    },
    [setShowSavedCards],
  );

  useEffect(() => {
    if (canUseSavedCard) {
      getSavedCards(checkoutClient).then((data) => {
        setSavedCards(data);
        const defaultCard = data.find((card) => card.default);
        if (defaultCard) {
          setSelectedCard(defaultCard);
          setPayWithSavedCard(true);
          setShowCardForm(false);
        }
      });

      if (sqCard) {
        sqCard.addEventListener('focusClassAdded', showSavedCardsDropdown);
        sqCard.addEventListener('cardBrandChanged', hideSavedCardsDropdown);
        sqCard.addEventListener('escape', hideSavedCardsDropdown);
      }
    }

    return () => {
      if (canUseSavedCard && sqCard) {
        sqCard.removeEventListener('focusClassAdded', showSavedCardsDropdown);
        sqCard.removeEventListener('cardBrandChanged', hideSavedCardsDropdown);
        sqCard.removeEventListener('escape', hideSavedCardsDropdown);
      }
    };
  }, [canUseSavedCard, sqCard, showSavedCardsDropdown, hideSavedCardsDropdown]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return {
    showSavedCards,
    savedCards,
    savedCardsDropdownRef,
    deletionInProgress,
    handlers: {
      onCardSelected,
      onCardClicked,
      onCardDeleted,
      showSavedCardsDropdown,
      hideSavedCardsDropdown,
      handleClickOutside,
    },
  };
};
